import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { uploadFile } from "@/lib/upload";

const EMPTY = { title: "", author: "", category: "samhita", description: "", content: "", pdf_url: "", cover_image: "" };

export const SamhitaTab = ({ samhita, refresh }) => {
  const [sam, setSam] = useState(EMPTY);
  const [open, setOpen] = useState(false);

  const onPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { url } = await uploadFile(f, "samhita");
      setSam((s) => ({ ...s, pdf_url: url }));
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { url } = await uploadFile(f, "samhita", { maxWidth: 900 });
      setSam((s) => ({ ...s, cover_image: url }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const save = async () => {
    try {
      await api.post("/samhita", sam);
      toast.success("Added to library");
      setSam(EMPTY); setOpen(false); refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/samhita/${id}`);
    refresh();
    toast.success("Deleted");
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-2xl">Library Texts</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground btn-pill" data-testid="adm-sam-add">
              <Plus size={14} className="mr-1"/> Add Text
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">New Library Entry</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={sam.title} onChange={(e)=>setSam({...sam,title:e.target.value})} data-testid="sam-title"/></div>
              <div><Label>Author</Label><Input value={sam.author} onChange={(e)=>setSam({...sam,author:e.target.value})} data-testid="sam-author"/></div>
              <div>
                <Label>Category</Label>
                <Select value={sam.category} onValueChange={(v)=>setSam({...sam,category:v})}>
                  <SelectTrigger data-testid="sam-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["samhita", "sutra", "article"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={sam.description} onChange={(e)=>setSam({...sam,description:e.target.value})} data-testid="sam-desc"/></div>
              <div><Label>Article Content (optional)</Label><Textarea rows={6} value={sam.content} onChange={(e)=>setSam({...sam,content:e.target.value})} placeholder="Plain text article…" data-testid="sam-content"/></div>
              <div><Label>PDF File (optional)</Label><Input type="file" accept="application/pdf" onChange={onPdf} data-testid="sam-pdf"/></div>
              <div><Label>Cover Image</Label><Input type="file" accept="image/*" onChange={onCover} data-testid="sam-cover"/></div>
            </div>
            <Button onClick={save} className="bg-primary text-primary-foreground mt-4" data-testid="sam-save">Save Entry</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {samhita.map((s) => (
          <Card key={s.id} className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-xl">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{s.author}</div>
                  <Badge variant="outline" className="text-[10px] capitalize mt-2">{s.category}</Badge>
                </div>
                <button onClick={() => remove(s.id)} className="text-destructive" data-testid={`sam-del-${s.id}`}><Trash2 size={14}/></button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default SamhitaTab;
